import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { StatusColumn } from '../columns/StatusColumn';
import { NameColumn } from '../columns/NameColumn';
import { DateColumn } from '../columns/DateColumn';
import { InfoColumn } from '../columns/InfoColumn';
import { ActionsColumn } from '../columns/ActionsColumn';
import {
  getStatusColumnMinWidth,
  getNameColumnWidth,
  getInfoColumnWidth,
  getFontSize
} from '../utils';
import { Text } from '@/ui/typography';
import { COLORS } from '@/utils/static';

export const useTableColumns = ({
  windowWidth,
  isVerification,
  adminView,
  isDocumentTable,
  isOpenButtonDisabled,
  isOpenButtonEnabled,
  referralView,
  onClick,
  onClickEdit,
  onDelete,
  downloadPdf,
  isDocumentsFromQuestionnaires = false,
  activeTab
}) => {
  const { t } = useTranslation();
  const investor = useSelector((state) => state.investor);
  const navigate = useNavigate();
  const location = useLocation();

  const columns = [
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      fixed: 'left',
      minWidth: getStatusColumnMinWidth(windowWidth),
      render: (status, record) => (
        <StatusColumn status={status} name={record.name} windowWidth={windowWidth} />
      )
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: getNameColumnWidth(windowWidth),
      render: (name) => <NameColumn name={name} windowWidth={windowWidth} />
    },
    {
      title: t('Created'),
      dataIndex: 'created',
      key: 'created',
      render: (created) => <DateColumn date={created} windowWidth={windowWidth} />
    },
    {
      title: t('Updated'),
      dataIndex: 'updated',
      key: 'updated',
      render: (updated) => <DateColumn date={updated} windowWidth={windowWidth} />
    },
    {
      title: t('Person/Company'),
      dataIndex: 'companyOrPerson',
      key: 'companyOrPerson',
      render: (_, { company, person }) => (
        <Text $fontSize={getFontSize(windowWidth)}>{company ?? person ?? '-'}</Text>
      ),
      hidden: isVerification
    },
    {
      title: t('Comment'),
      key: 'comment',
      dataIndex: 'error',
      render: (error) => (
        <Text $color={COLORS.red} $fontSize={getFontSize(windowWidth)}>
          {error ?? ''}
        </Text>
      ),
      hidden: activeTab !== 'rejected'
    },
    {
      title: t('Info'),
      dataIndex: 'info',
      key: 'info',
      width: getInfoColumnWidth(windowWidth),
      render: (_, record) => (
        <InfoColumn
          company={record.company}
          person={record.person}
          documentType={record.documentType}
          documentNumber={record.documentNumber}
          verificationId={record.verificationId}
          expiration={record.expiration}
          windowWidth={windowWidth}
        />
      ),
      hidden: !isVerification
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <ActionsColumn
          record={record}
          adminView={adminView}
          isVerification={isVerification}
          isDocumentTable={isDocumentTable}
          disabled={isOpenButtonDisabled}
          isOpenButtonEnabled={isOpenButtonEnabled}
          referralView={referralView}
          investorAccountStatus={investor.accountStatus}
          investorId={investor.id}
          navigate={navigate}
          location={location}
          onClick={isDocumentTable ? onClick : record.onClick}
          onClickEdit={record.onClickEdit}
          onClickDelete={record.onClickDelete}
          onDelete={onDelete}
          downloadPdf={downloadPdf}
          isDocumentsFromQuestionnaires={isDocumentsFromQuestionnaires}
        />
      )
    }
  ];

  return columns.filter((column) => !column.hidden);
};
